import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Table,
} from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { formatTrx, formatUsd, formatNumber } from '@/lib/utils';
import { ProjectionRow } from '@/types';

export default function DetailsTable() {
  const { rows } = useAppStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<keyof ProjectionRow>('day');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const rowsPerPage = 20;

  if (rows.length === 0) {
    return (
      <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
        <h2 className="text-lg font-semibold mb-4 text-white">
          Daily Projections
        </h2>
        <div className="text-slate-400 text-center py-8">
          Configure your transfer settings to see detailed daily projections.
        </div>
      </div>
    );
  }

  const columns = [
    { key: 'day', label: 'Day', sortable: true },
    { key: 'tpd', label: 'Transfers/Day', sortable: true },
    { key: 'energyDay', label: 'Energy/Day', sortable: true },
    { key: 'trxNeeded', label: 'TRX for Energy', sortable: true },
    { key: 'bandwidthDay', label: 'Bandwidth/Day', sortable: true },
    { key: 'trxForBandwidth', label: 'TRX for Bandwidth', sortable: true },
    { key: 'trxBurned', label: 'TRX Burned (if under-staked)', sortable: true },
    { key: 'totalTrxNeeded', label: 'Total TRX Needed', sortable: true },
    {
      key: 'dailyCostWithoutStaking',
      label: 'Daily Cost Without Staking (TRX)',
      sortable: true,
    },
    { key: 'savingsVsNoStake', label: 'Daily Savings (USD)', sortable: true },
    { key: 'dailyCost', label: 'Daily Cost (USD)', sortable: true },
    { key: 'cumulativeCost', label: 'Cumulative Cost (USD)', sortable: true },
  ];

  // Sort rows
  const sortedRows = [...rows].sort((a, b) => {
    const aValue = a[sortField] as any;
    const bValue = b[sortField] as any;

    // Handle string values
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // Handle number values
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // Handle boolean values
    if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
      return sortDirection === 'asc'
        ? aValue === bValue
          ? 0
          : aValue
            ? 1
            : -1
        : aValue === bValue
          ? 0
          : aValue
            ? -1
            : 1;
    }

    return 0;
  });

  const totalPages = Math.ceil(sortedRows.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedRows.slice(startIndex, endIndex);

  const handleSort = (field: keyof ProjectionRow) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: keyof ProjectionRow }) => {
    if (sortField !== field) {
      return <ChevronDown className="h-4 w-4 text-slate-400" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4 text-blue-400" />
    ) : (
      <ChevronDown className="h-4 w-4 text-blue-400" />
    );
  };

  return (
    <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Daily Projections</h2>
          <p className="text-slate-400">
            Detailed breakdown of your daily resource requirements and costs
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
            <Table className="h-5 w-5 text-blue-400" />
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-slate-800/50 rounded-lg mb-6 border border-slate-700/50">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-400">
            {formatTrx(rows.reduce((sum, row) => sum + row.totalTrxNeeded, 0))}
          </div>
          <div className="text-sm text-slate-300">Total TRX Required</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-red-400">
            {formatTrx(rows.reduce((sum, row) => sum + row.trxBurned, 0))}
          </div>
          <div className="text-sm text-slate-300">Total TRX Burned</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-green-400">
            {formatUsd(
              rows.reduce((sum, row) => sum + row.savingsVsNoStake, 0)
            )}
          </div>
          <div className="text-sm text-slate-300">Total Savings</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-red-400">
            {formatUsd(rows.reduce((sum, row) => sum + row.dailyCost, 0))}
          </div>
          <div className="text-sm text-slate-300">Total Cost</div>
        </div>

        <div className="text-center">
          <div className="text-2xl font-bold text-orange-400">
            {formatTrx(
              rows.reduce((sum, row) => sum + row.dailyCostWithoutStaking, 0)
            )}
          </div>
          <div className="text-sm text-slate-300">
            Total Daily Cost Without Staking
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700/50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`text-left p-3 font-medium text-slate-300 ${
                    column.sortable
                      ? 'cursor-pointer hover:bg-slate-700/50'
                      : ''
                  }`}
                  onClick={() =>
                    column.sortable &&
                    handleSort(column.key as keyof ProjectionRow)
                  }
                >
                  <div className="flex items-center space-x-1">
                    <span className="text-slate-300">{column.label}</span>
                    {column.sortable && (
                      <SortIcon field={column.key as keyof ProjectionRow} />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row, index) => (
              <tr
                key={index}
                className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors"
              >
                {columns.map((column) => (
                  <td key={column.key} className="p-3">
                    <span className="text-slate-200">
                      {typeof row[column.key as keyof ProjectionRow] ===
                      'number'
                        ? formatNumber(
                            row[column.key as keyof ProjectionRow] as number
                          )
                        : String(row[column.key as keyof ProjectionRow])}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-slate-400">
            Showing {startIndex + 1} to {Math.min(endIndex, rows.length)} of{' '}
            {rows.length} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-slate-300">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Table Legend */}
      <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
        <h4 className="font-medium mb-3 text-white">Understanding the Data</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-300">
          <div>
            <h5 className="font-medium text-blue-400 mb-2">
              Resource Requirements
            </h5>
            <ul className="space-y-1 text-slate-400">
              <li>
                • <strong>Energy/Day:</strong> Total energy units needed for
                daily transfers
              </li>
              <li>
                • <strong>Bandwidth/Day:</strong> Total bandwidth bytes needed
                for daily transfers
              </li>
              <li>
                • <strong>TRX for Energy:</strong> TRX needed to stake for
                energy coverage
              </li>
              <li>
                • <strong>TRX for Bandwidth:</strong> TRX needed to stake for
                bandwidth coverage
              </li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-green-400 mb-2">Cost Analysis</h5>
            <ul className="space-y-1 text-slate-400">
              <li>
                • <strong>TRX Burned:</strong> TRX burned if you don't stake
                enough
              </li>
              <li>
                • <strong>Daily Cost:</strong> USD cost of daily fees without
                staking
              </li>
              <li>
                • <strong>Daily Savings:</strong> USD saved daily by staking TRX
              </li>
              <li>
                • <strong>Cumulative Cost:</strong> Total cost accumulated over
                time
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
